import { Injectable, Inject } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, switchMap, tap, take } from 'rxjs/operators';
import { OrderActions } from '../actions';
import { Order } from '../client/src';
import { OrderStoreFacade } from '../facade';
import {
  FaresActions,
  TicketsAndFaresStoreFacade,
} from '@modules/ticket-fares-store';
import { OrderService } from '../services';
import { ApiError } from '@modules/checkout-store';
import {
  GtmEventUser,
  ICorrelationParams,
  IOrderTicket,
  ITicketProductWithBundleType,
  LoggingService,
} from '@nx-ng/types';
import { LOGGER } from '@modules/core';
import { aggregate } from '@modules/checkout-store/util';
import { v4 as uuidv4 } from 'uuid';
import { B2CEventsService } from '@nx-ng/b2c/b2c-events.service';
import { CustomerStoreFacade } from '@modules/customer-store';
import { CoreFacade } from '@modules/core/core.facade';
import { GtmEventService } from '@nx-ng/services/gtm-event.service';
import { MsalPolicyService } from '@modules/core/services/msalPolicy.service';

@Injectable()
export class OrderStoreEffects {
  private userDetails$: Observable<GtmEventUser> = combineLatest([
    this.b2cService.isLoggedIn$,
    this.customerStoreFacade.customerId$,
  ]).pipe(
    switchMap(
      ([isLoggedIn, customerId]: [boolean | undefined, string | undefined]) => {
        const gtmUser: GtmEventUser = {
          loginState: isLoggedIn === true,
          id: customerId,
          loginMethod: 'email',
        };

        return of(gtmUser);
      }
    )
  );

  loadOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.orderAPI_loadOrder),
      switchMap((action: { orderNumber: string } & Action) =>
        combineLatest([of(action), this.msalPolicyService.token$]).pipe(
          switchMap(([action, token]) => {
            const { orderNumber } = action;

            if (!token) {
              return EMPTY;
            }
            return this.orderService.loadOrder(orderNumber, token).pipe(
              switchMap((order?: Order) => {
                if (!order) {
                  return of(
                    OrderActions.orderAPI_loadOrderFailure({
                      error: 'Empty order response received',
                    })
                  );
                }
                return [
                  OrderActions.orderAPI_loadOrderSuccess({
                    response: order,
                  }),
                  OrderActions.loadOrderTickets({
                    correlationParams: {
                      correlationId: uuidv4(),
                    },
                  }),
                ];
              }),
              catchError((error) => {
                console.log('err', error);

                const apiError: ApiError | string =
                  error?.response?.parsedBody || error;

                this.logger.logApiError(
                  {
                    apiError,
                  },
                  error
                );

                return of(
                  OrderActions.orderAPI_loadOrderFailure({
                    error: apiError,
                    retryAction: action,
                  })
                );
              })
            );
          })
        )
      )
    )
  );

  loadOrderTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.loadOrderTickets),
      switchMap((action) =>
        combineLatest([this.orderFacade.orderTicketIds$, of(action)])
      ),
      switchMap(
        ([tickets, action]: [
          tickets: string[],
          action: { correlationParams?: ICorrelationParams }
        ]) => {
          return tickets.map((id: string) => {
            return FaresActions.loadFareById({
              ticketID: id,
              correlationParams: {
                correlationId: action.correlationParams?.correlationId,
                parentActionType: OrderActions.loadOrderTickets.type,
              },
            });
          });
        }
      )
    )
  );

  sendTicketReceiptGtm = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrderActions.loadOrderTickets),
        switchMap((action) =>
          combineLatest([this.orderFacade.orderTicketIds$, of(action)])
        ),
        switchMap(([tickets, action]: [tickets: string[], action: any]) => {
          return of(action).pipe(
            aggregate(
              this.actions$.pipe(ofType(FaresActions.loadFareByIdSuccess)),
              tickets.length,
              this.actions$.pipe(ofType(OrderActions.loadOrderTicketsFailure))
            ) //Wait for all ticket to load
          );
        }),
        switchMap(() => {
          return combineLatest([
            this.coreFacade.gtmEnabled$,
            this.ticketAndFaresFacade.allFares$,
            this.orderFacade.order$,
            this.userDetails$,
            this.orderFacade.orderTickets$,
          ]).pipe(take(1));
        }),
        tap(
          ([gtmEnabled, allFares, order, userDetails, orderTickets]: [
            boolean,
            ITicketProductWithBundleType[],
            Order | undefined,
            GtmEventUser,
            IOrderTicket[]
          ]) => {
            if (!gtmEnabled) {
              return;
            }
            this.gtmEventService.sendCheckoutEvent({
              allTickets: allFares,
              order,
              orderTickets,
              userDetails,
            });
          }
        )
      ),

    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private b2cService: B2CEventsService,
    private coreFacade: CoreFacade,
    private orderFacade: OrderStoreFacade,
    private orderService: OrderService,
    private ticketAndFaresFacade: TicketsAndFaresStoreFacade,
    private customerStoreFacade: CustomerStoreFacade,
    private gtmEventService: GtmEventService,
    private msalPolicyService: MsalPolicyService,
    @Inject(LOGGER) private logger: LoggingService
  ) {}
}
